import { z } from 'zod';

import type { BinaryAnswer } from '@/constants/answers';
import i18n from '@/lib/i18n';
import {
  type InstitutionDetailsV2AmlCtfSanctions,
  institutionDetailsV2AmlCtfPolicyDocumentSchema,
  institutionDetailsV2AmlCtfSanctionsAnswersSchema,
  institutionDetailsV2AmlCtfSanctionsSchema,
  institutionDetailsV2SanctionsPolicyDocumentSchema,
} from '@/services/api/profile';

export type InstitutionAmlCtfSanctions = {
  amlCtfPolicies?: BinaryAnswer;
  amlCtfPolicyDocument?: string;
  sanctionsPolicies?: BinaryAnswer;
  sanctionsPolicyDocument?: string;
  screensMajorSanctionsLists?: BinaryAnswer;
  assessesPepRisk?: BinaryAnswer;
  screensSanctionsPepAdverseMedia?: BinaryAnswer;
  hasEscalationProcess?: BinaryAnswer;
  hasSuspiciousActivityPolicies?: BinaryAnswer;
  transactionMonitoringMethods: string;
  hasStrReportingObligation?: BinaryAnswer;
  hasEmployeeEscalationProcess?: BinaryAnswer;
  prohibitsAnonymousAccounts?: BinaryAnswer;
  prohibitsUnlicensedBankAccounts?: BinaryAnswer;
  prohibitsServicesToUnlicensedBanks?: BinaryAnswer;
  prohibitsShellBanks?: BinaryAnswer;
  prohibitsServicesToShellBanks?: BinaryAnswer;
  policiesApprovedAnnually?: BinaryAnswer;
  recordRetentionCompliant?: BinaryAnswer;
  independentAmlAudit?: BinaryAnswer;
  itAndSoftwareProviders: string;
};

export type InstitutionAmlCtfSanctionsOrDefaults = InstitutionAmlCtfSanctions | Partial<InstitutionAmlCtfSanctions>;

export const INSTITUTION_AML_CTF_SANCTIONS_DEFAULTS: InstitutionAmlCtfSanctions = {
  transactionMonitoringMethods: '',
  itAndSoftwareProviders: '',
};

const institutionAmlCtfSanctionsPoliciesSchema = z
  .discriminatedUnion(
    'amlCtfPolicies',
    [
      z.object({
        amlCtfPolicies: z.literal('NO'),
      }),
      z.object({
        amlCtfPolicies: z.literal('YES'),
        amlCtfPolicyDocument: institutionDetailsV2AmlCtfPolicyDocumentSchema,
      }),
    ],
    { error: i18n.t('institutionDetails.amlCtfSanctions.errors.amlCtfPolicies') },
  )
  .and(
    z.discriminatedUnion(
      'sanctionsPolicies',
      [
        z.object({
          sanctionsPolicies: z.literal('NO'),
        }),
        z.object({
          sanctionsPolicies: z.literal('YES'),
          sanctionsPolicyDocument: institutionDetailsV2SanctionsPolicyDocumentSchema,
        }),
      ],
      { error: i18n.t('institutionDetails.amlCtfSanctions.errors.sanctionsPolicies') },
    ),
  );

export const institutionAmlCtfSanctionsPoliciesScreeningAndMonitoringSchema = institutionAmlCtfSanctionsPoliciesSchema.and(
  institutionDetailsV2AmlCtfSanctionsAnswersSchema.pick({
    screensMajorSanctionsLists: true,
    assessesPepRisk: true,
    screensSanctionsPepAdverseMedia: true,
    hasEscalationProcess: true,
    hasSuspiciousActivityPolicies: true,
    transactionMonitoringMethods: true,
    hasStrReportingObligation: true,
    hasEmployeeEscalationProcess: true,
  }),
);

export const institutionAmlCtfSanctionsProhibitedRelationshipsAndGovernanceSchema = institutionDetailsV2AmlCtfSanctionsAnswersSchema.pick({
  prohibitsAnonymousAccounts: true,
  prohibitsUnlicensedBankAccounts: true,
  prohibitsServicesToUnlicensedBanks: true,
  prohibitsShellBanks: true,
  prohibitsServicesToShellBanks: true,
  policiesApprovedAnnually: true,
  recordRetentionCompliant: true,
  independentAmlAudit: true,
  itAndSoftwareProviders: true,
});

// Parsing drops a policy document left over from an earlier Yes
export const institutionAmlCtfSanctionsToInstitutionDetailsAmlCtfSanctionsApi = (amlCtfSanctions: InstitutionAmlCtfSanctions): Promise<InstitutionDetailsV2AmlCtfSanctions> =>
  institutionDetailsV2AmlCtfSanctionsSchema.parseAsync(amlCtfSanctions);
